/**
 * Компенсації відпустки при звільненні — список (канон Норми #29, 2026-08-23):
 * InfiniteDocGrid (серверна пагінація/сортування, ПІБ з сервера), фільтр орг
 * рядком під назвою, колонка «Підпис» (sign-control doc.vacation_compensations).
 */
import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import { vacationCompensation, type VacationCompensationItem } from '@/api/vacation';
import { orgs } from '@/api/catalogs';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { useSignControlInfinite } from '@/shared/hooks/useSignControl';
import { useDefaults } from '@/store/defaults';

type Row = VacationCompensationItem & { employee_name?: string | null };

const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const money = (p: { value?: string | null }) => p.value != null ? fmt2(p.value) : '';

function extractError(e: unknown): string {
  const err = e as { response?: { data?: { detail?: string } }; message?: string };
  return err?.response?.data?.detail ?? err?.message ?? 'Помилка';
}

export default function VacationCompensationsPage() {
  const navigate = useNavigate();
  // 2026-07-26: старт з організації за замовчуванням з шапки (було «оберіть»).
  const [orgId, setOrgId] = useState(() => useDefaults.getState().defaultOrgId);
  const [error, setError] = useState<string | null>(null);
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });

  const refreshRef = useRef<(() => void) | null>(null);
  const { datasource, total } = useInfiniteGridDatasource<Row>(
    async (limit, offset, sort) => {
      if (!orgId) return { items: [], total: 0 };
      return vacationCompensation.page({
        organization_id: orgId, sort_by: sort?.sort_by, sort_dir: sort?.sort_dir, limit, offset,
      });
    },
    [orgId],
    { docType: 'doc.vacation_compensations' },
  );
  const sc = useSignControlInfinite<Row>('doc.vacation_compensations', orgId);

  const postMut = useMutation({
    mutationFn: (id: string) => vacationCompensation.post(id),
    onSuccess: () => { setError(null); refreshRef.current?.(); },
    onError: (e) => setError(extractError(e)),
  });
  const unpostMut = useMutation({
    mutationFn: (id: string) => vacationCompensation.unpost(id),
    onSuccess: () => { setError(null); refreshRef.current?.(); },
    onError: (e) => setError(extractError(e)),
  });

  const cols = useMemo<ColDef<Row>[]>(() => [
    { field: 'document_number', headerName: '№', width: 110 },
    { field: 'employee_name', headerName: 'Працівник', flex: 1, minWidth: 200,
      valueFormatter: (p) => p.data ? (p.value ?? '—') : '' },
    {
      field: 'dismiss_date', headerName: 'Дата звільнення', width: 150,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleDateString('uk-UA') : '',
    },
    {
      colId: 'order', headerName: 'Наказ', width: 110, sortable: false,
      cellRenderer: (p: { data?: Row }) => p.data ? (
        <a href={`/dismissal-orders/${p.data.dismissal_order_id}`}
          onClick={(e) => { e.preventDefault(); navigate(`/dismissal-orders/${p.data!.dismissal_order_id}`); }}
          style={{ color: '#2563eb', cursor: 'pointer' }}>Відкрити →</a>
      ) : '',
    },
    { field: 'compensation_days', headerName: 'Днів', width: 80, cellStyle: { textAlign: 'right' }, valueFormatter: money },
    { field: 'average_daily_wage', headerName: 'Середня', width: 110, cellStyle: { textAlign: 'right' }, valueFormatter: money },
    { field: 'compensation_gross_amount', headerName: 'Нараховано', width: 120, cellStyle: { textAlign: 'right' }, valueFormatter: money },
    { field: 'net_amount', headerName: 'До виплати', width: 120, cellStyle: { textAlign: 'right' }, valueFormatter: money },
    {
      field: 'posted', headerName: 'Проведено', width: 100,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { posted?: boolean } }) =>
        p.data?.posted ? <span style={{ color: 'var(--badge-green-fg)', fontWeight: 600 }}>✓</span> : '',
    },
    {
      colId: 'actions', headerName: 'Дія', width: 130, sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: Row }) => !p.data ? '' : (
        p.data.posted ? (
          <button onClick={() => unpostMut.mutate(p.data!.id)} disabled={unpostMut.isPending}>
            розпровести
          </button>
        ) : (
          <button className="primary" onClick={() => postMut.mutate(p.data!.id)} disabled={postMut.isPending}>
            провести
          </button>
        )
      ),
    },
  ], [navigate, postMut, unpostMut]);

  return (
    <div className="card">
      <div className="toolbar with-filters" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
        <h2 style={{ margin: 0 }}>Компенсації відпустки при звільненні<TitleTotal total={total} /></h2>
        <button className="primary" style={{ marginLeft: 'auto' }}
          title="Створити нарахування компенсації (оберіть наказ про звільнення)"
          onClick={() => navigate('/hr/vacation-compensations/new')}>
          + Нове нарахування
        </button>
      </div>
      <div className="filters" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', margin: '0 0 0.6rem', alignItems: 'center' }}>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={{ maxWidth: 260 }}>
          <option value="">— оберіть організацію —</option>
          {orgList.data?.items.map((o) => <option key={o.id} value={o.id}>{o.name} — {o.code}</option>)}
        </select>
        <span style={{ color: 'var(--muted)', fontSize: '0.82rem' }}>
          Компенсація створюється з наказу звільнення; дабл-клік по рядку — відкрити чернетку.
        </span>
      </div>
      {error && <div style={{ background: 'var(--badge-red-bg)', color: '#b00', padding: '0.4rem 0.6rem', borderRadius: 6, marginBottom: '0.5rem' }}>{error}</div>}
      {orgId ? (
        <InfiniteDocGrid<Row>
          datasource={sc.wrap(datasource)}
          columnDefs={[...cols, ...sc.column]}
          storageKey="vacation-compensations"
          onRowDoubleClicked={(r) => navigate(`/hr/vacation-compensations/${r.id}`)}
          getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : sc.getRowStyle(p)}
          refreshRef={refreshRef}
        />
      ) : (
        <p style={{ color: 'var(--muted)' }}>Оберіть організацію.</p>
      )}
    </div>
  );
}
